import React, { useState, useEffect, useContext } from "react";
import { hot } from "react-hot-loader/root";
import { range } from "d3-array";
import theme from "~/scripts/theme";

import * as S from "./Drawer.styles.js";
import { Button, MobileOnly, DesktopOnly } from "~/styles/index.styles";
import { is768Query } from "~/core/scripts/mediaquery-helper";
import { TranslationContext } from "~/kits/general/scripts/app.js";

import Board from "~/components/Board";
import Message from "~/components/Message";
import ElectionGrid from "~/components/Board/layers/ElectionGrid";
import Icon from "~/core/scripts/components/Icon";
import DrawLayer from "./DrawLayer";
import ResultBoard from "~/components/ResultBoard";
import Elector from "~/components/Elector";
import Solution from "./Solution";

const Drawer = () => {
  const polyglot = useContext(TranslationContext);
  const isDesktop = is768Query();
  const [showDrawEnableOverlay, setDrawEnableOverlay] = useState(true);
  const [drawResult, setDrawResult] = useState({ isFinished: false });
  const [drawingError, setDrawingError] = useState(false);
  const [resultState, setResultState] = useState([]);
  const [solutionIsVisible, setSolutionIsVisible] = useState(false);

  const undoLast = () => {
    setResultState(resultState.slice(0, resultState.length - 1));
    setDrawingError(false);
  };

  const restart = () => {
    setResultState([]);
    setDrawingError(false);
    setDrawResult({ isFinished: false });
  };

  useEffect(() => {
    if (
      (drawingError || drawResult.trumpWon || drawResult.bidenWon) &&
      !isDesktop
    ) {
      document
        .querySelector("#scroll-here-after-drawing")
        .scrollIntoView({ behavior: "smooth" });
    }
  }, [drawingError, drawResult.trumpWon, drawResult.bidenWon]);

  const isDisabled = drawingError || drawResult.trumpWon || drawResult.bidenWon;

  const removeOverlay = () => {
    setDrawEnableOverlay(false);
  };

  const results = (
    <S.DrawerResults>
      <ResultBoard {...drawResult} />
      <S.ElectorsWrapper style={{ opacity: drawResult.isFinished ? 1 : 0.2 }}>
        <S.ElectorsTitle>{polyglot.t("elected")}</S.ElectorsTitle>
        {range(3).map((i) => (
          <Elector
            key={`elector-${i}`}
            color={
              drawResult.isFinished
                ? drawResult.bidenOverallVotes <= i
                  ? theme.colors.republican
                  : theme.colors.democratic
                : undefined
            }
            number={i + 1}
          />
        ))}
      </S.ElectorsWrapper>
    </S.DrawerResults>
  );

  return (
    <>
      <S.DrawerWrapper>
        <S.DrawerBoard isDisabled={isDisabled}>
          <Board>
            <S.DrawerOverlay
              isOverlay={showDrawEnableOverlay}
              onClick={removeOverlay}
              onMouseOver={removeOverlay}
            />
            <ElectionGrid small={!isDesktop} />
            <DrawLayer
              resultState={resultState}
              setResultState={setResultState}
              onError={() => setDrawingError(true)}
              onResult={(res) =>
                setDrawResult({
                  ...res,
                  isFinished: res.trumpWon || res.bidenWon,
                })
              }
            />
          </Board>
        </S.DrawerBoard>
        <S.DrawerExplanation id="scroll-here-after-drawing">
          {!drawResult.isFinished && !drawingError && (
            <>
              <S.InitialText>
                <S.Left>
                  <Icon
                    type={isDesktop ? "swoopy-down-left" : "swoopy-left"}
                    style={{
                      display:
                        resultState.length > 0 && !isDesktop ? "none" : "block",
                    }}
                  />
                  <MobileOnly>
                    {resultState.length > 0 && (
                      <div>
                        <Button
                          onClick={restart}
                          style={{ margin: "12px 0 0" }}
                        >
                          <Icon type="refresh" /> {polyglot.t("startOver")}
                        </Button>
                      </div>
                    )}
                  </MobileOnly>
                </S.Left>
                <S.Right>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: polyglot.t("initialText"),
                    }}
                  />
                  {resultState.length > 0 && (
                    <DesktopOnly>
                      <Button onClick={restart} style={{ margin: "12px 0" }}>
                        <Icon type="refresh" /> {polyglot.t("startOver")}
                      </Button>
                    </DesktopOnly>
                  )}
                </S.Right>
              </S.InitialText>
            </>
          )}
          {drawingError && (
            <Message type="warning" title={polyglot.t("warningTitle")}>
              <span
                dangerouslySetInnerHTML={{ __html: polyglot.t("warningText") }}
              />
              <S.RestartButton onClick={undoLast}>
                <Icon type="refresh" /> {polyglot.t("undoLast")}
              </S.RestartButton>
            </Message>
          )}
          {drawResult.trumpWon && (
            <Message type="trumpWon" title={polyglot.t("trumpWonTitle")}>
              <span
                dangerouslySetInnerHTML={{ __html: polyglot.t("trumpWonText") }}
              />
              <S.RestartButton onClick={restart}>
                <Icon type="refresh" /> {polyglot.t("startOver")}
              </S.RestartButton>
            </Message>
          )}
          {drawResult.bidenWon && (
            <Message type="bidenWon" title={polyglot.t("bidenWonTitle")}>
              <span
                dangerouslySetInnerHTML={{ __html: polyglot.t("bidenWonText") }}
              />
              <S.RestartButton onClick={restart}>
                <Icon type="refresh" /> {polyglot.t("startOver")}
              </S.RestartButton>
            </Message>
          )}
          <DesktopOnly>{results}</DesktopOnly>
        </S.DrawerExplanation>
      </S.DrawerWrapper>
      <MobileOnly>{results}</MobileOnly>
      <S.ShowSolutionWrapper>
        <Button onClick={() => setSolutionIsVisible(!solutionIsVisible)}>
          {solutionIsVisible
            ? polyglot.t("hideSolution")
            : polyglot.t("showSolution")}
        </Button>
      </S.ShowSolutionWrapper>
      <S.SolutionWrapper isVisible={solutionIsVisible}>
        <Solution />
      </S.SolutionWrapper>
    </>
  );
};

export default hot(Drawer);
